/* eslint-disable camelcase */
import MiniLoader from 'ageas-react-component-miniloader'
import moment from 'moment'
import React, { useEffect, useReducer } from 'react'

import { useAnimalStore } from '../../Stores/animalStore'
import { useAnimalValidateStore } from '../../Stores/useAnimalValidateStore'
import { useFillDataForSimulationStore } from '../../Stores/useFillDataForSimulationStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { useResumeStore } from '../../Stores/useResumeStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { useWarningStore } from '../../Stores/warningStore'
import { AnimalsSecured } from '../../Types/AnimalStore'
import { t } from '../../i18n'
import { removePropertiesImmutably } from '../../utils/validates'
import { Minus, Plus, Trash } from '../Icons'
import { AnimalTab } from './AnimalTab'
import { TYPES, initialState, reducer } from './reducer'
import {
  Actions,
  ActionsCell,
  ActionsHeader,
  AddAnimalButtonContainer,
  AddAnimalButtonText,
  AnimalInsuranceTableContainer,
  ButtonContainer,
  Cell,
  ExpandedRow,
  ExpandedRowContainer,
  Header,
  IdCell,
  IdHeader,
  Row,
  Table,
  TableActions,
  TableActionsContainer,
  TableActionsData,
  TableContainer,
  Title,
} from './styles'

const LIMIT_ANIMALS = 6

type Props = {
  species: any
  isLoading: boolean
  disabled?: boolean
  isError: boolean
  isValid: (value: { [key: string]: boolean }) => void
  setIsValid: (value: { [key: string]: boolean }, replace?: boolean) => void
}

export const AnimalInsuranceTable = ({
  isLoading,
  isError,
  disabled,
  isValid,
  setIsValid,
}: Props) => {
  const { animalsList, setNewAnimal, deleteAnimal, updateAnimal } =
    useAnimalStore()
  const { questionary } = useFillDataForSimulationStore()
  const { resetAddressOwner, deleteOldSpecies } = useAnimalValidateStore()
  const { isResume, isResumeLoading } = useResumeStore()
  const { setEnabledRunPetisRequest } = useRunPetisSimulationStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()
  const [state, dispatch] = useReducer(reducer, initialState)
  const { expandedRow } = state

  const { setWarningOptions } = useWarningStore()

  const isExpandedRowValid = () => {
    if (expandedRow !== null) {
      const animal = animalsList.find((animal) => animal.id === expandedRow)

      if (animal) {
        return Boolean(animal.birthDate && animal.species && animal.breed)
      }
    }
    return true
  }

  const messageError = () => {
    setWarningOptions({
      labelTypes: [t('pages.step1.errors.questionnaires')],
    })
  }

  const handleExpandClick = (animal: AnimalsSecured) => {
    if (isExpandedRowValid()) {
      dispatch({
        type: TYPES.setShowInvalidIcon,
        payload: false,
      })
      dispatch({
        type: TYPES.setExpandedRow,
        payload: expandedRow === animal.id ? null : animal.id,
      })
    } else {
      dispatch({
        type: TYPES.setShowInvalidIcon,
        payload: true,
      })
      messageError()
    }
  }

  const handleDeleteClick = (animal: AnimalsSecured) => {
    const newData = removePropertiesImmutably(isValid as any, [
      `questionsAnimal${animal.id}`,
      `formAnimal${animal.id}`,
    ])

    setIsValid(newData, true)

    deleteAnimal(animal.id)
    deleteOldSpecies(`species-${animal.id}`)
    resetAddressOwner(`address-${animal.id}`)
    setEnabledRunPetisRequest(true)
    dispatch({
      type: TYPES.setExpandedRow,
      payload: null,
    })
  }

  const addAnimal = () => {
    const newAnimalId = (Number(animalsList.at(-1)?.id || 0) + 1).toString()
    const newSafeAnimal: AnimalsSecured = {
      id: newAnimalId,
      OwnerId: 1, // TODO: ADD OWNER ID
      name: '',
      birthDate: '',
      species: '',
      breed: '',
      questionary,
      pack: {
        name: '',
        price: '',
        installment: '',
      },
    }

    setNewAnimal(newSafeAnimal)
    handleExpandClick(newSafeAnimal)
    setIsValid({
      [`questionsAnimal${newAnimalId}`]: false,
      [`formAnimal${newAnimalId}`]: false,
    })
  }

  const handleAddSafeAnimal = () => {
    if (disabled) return

    if (
      animalsList.length === 0 ||
      (animalsList.length > 0 && Object.values(isValid).every(Boolean))
    ) {
      dispatch({
        type: TYPES.setBreedsAnimals,
        payload: false,
      })
      dispatch({
        type: TYPES.setShowInvalidIcon,
        payload: false,
      })

      if (animalsList.at(-1)?.name?.length === 0) {
        updateAnimal(
          { name: `Animal${Number(animalsList.at(-1)?.id || 0)}` },
          animalsList.at(-1)?.id || '0',
        )
      }

      addAnimal()
    } else {
      dispatch({
        type: TYPES.setShowInvalidIcon,
        payload: true,
      })
      messageError()
    }
  }

  useEffect(() => {
    if (!isResumeLoading) {
      if (questionary.length && animalsList.length === 0 && !isResume) {
        handleAddSafeAnimal()
      }
    }
  }, [questionary, isResume])

  return (
    <AnimalInsuranceTableContainer>
      <Title>{t('pages.step1.animalInsuranceTable.title')}</Title>
      <TableContainer>
        <Table>
          <thead>
            <Row>
              <IdHeader></IdHeader>
              <Header>{t('table.name')}</Header>
              <Header>{t('table.birthday')}</Header>
              <Header>{t('table.species')}</Header>
              <Header>{t('table.breed')}</Header>
              <ActionsHeader></ActionsHeader>
            </Row>
          </thead>
          <tbody>
            {isLoading && (
              <Row>
                <Cell colSpan={6}>
                  <MiniLoader color="white" minHeight="auto" maxWidth="auto" />
                </Cell>
              </Row>
            )}
            {!isLoading &&
              animalsList.map((animal, index) => (
                <React.Fragment key={animal.id}>
                  <Row>
                    <IdCell>
                      {(animal.name || '')?.length > 0 && (
                        <div>{index + 1}</div>
                      )}
                    </IdCell>
                    <Cell>{animal.name}</Cell>
                    <Cell>
                      {animal.birthDate && animal.birthDate !== 'Invalid date'
                        ? moment(animal.birthDate).format('DD-MM-YYYY')
                        : ''}
                    </Cell>
                    <Cell>{animal.species}</Cell>
                    <Cell>{animal.breed}</Cell>
                    <ActionsCell>
                      <Actions>
                        {!isGenerateProposalDocuments && !disabled && (
                          <ButtonContainer
                            onClick={() => handleDeleteClick(animal)}
                          >
                            <Trash />
                          </ButtonContainer>
                        )}
                        <ButtonContainer
                          onClick={() => handleExpandClick(animal)}
                        >
                          {expandedRow === animal.id ? (
                            <Minus color="#333" size={24} />
                          ) : (
                            <Plus color="#333" size={24} />
                          )}
                        </ButtonContainer>
                      </Actions>
                    </ActionsCell>
                  </Row>
                  {expandedRow === animal.id && (
                    <ExpandedRowContainer>
                      <ExpandedRow colSpan={7}>
                        <AnimalTab
                          animal={animal}
                          setIsValid={setIsValid}
                          disabled={disabled}
                        />
                      </ExpandedRow>
                    </ExpandedRowContainer>
                  )}
                </React.Fragment>
              ))}
            <TableActionsContainer>
              <TableActionsData colSpan={7}>
                <TableActions></TableActions>
              </TableActionsData>
            </TableActionsContainer>
          </tbody>
        </Table>
      </TableContainer>
      {!isGenerateProposalDocuments &&
        !isError &&
        !isLoading &&
        animalsList.length <= LIMIT_ANIMALS && (
          <AddAnimalButtonContainer>
            <div
              className={`button ${disabled ? 'disabled' : ''}`}
              onClick={() => handleAddSafeAnimal()}
            >
              <Plus color="#333" size={24} />
              <AddAnimalButtonText>
                {t('pages.step1.animalInsuranceTable.addAnimal')}
              </AddAnimalButtonText>
            </div>
          </AddAnimalButtonContainer>
        )}
    </AnimalInsuranceTableContainer>
  )
}
